import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CheckCircle, XCircle, Clock, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const h = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('hrms-token')}`,
});

function StatusBadge({ status }) {
  if (status === 'pending') return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  if (status === 'approved') return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
  if (status === 'rejected') return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

export function CorrectionRequestsAdmin({ isSuperAdmin = false }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewItem, setReviewItem] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (status) => {
    try {
      const url = status && status !== 'all' ? `${API_URL}/api/employee-corrections?status=${status}` : `${API_URL}/api/employee-corrections`;
      const res = await fetch(url, { headers: h(), credentials: 'include' });
      if (res.ok) setItems(await res.json());
    } catch {}
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const openReview = (item) => {
    setReviewItem(item);
    setNotes('');
    setReviewOpen(true);
  };

  const decide = async (action) => {
    if (!reviewItem) return;
    if (action === 'reject' && !notes.trim()) {
      toast.error('Please provide a reason for rejection.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/employee-corrections/${reviewItem.correction_id}/${action}`, {
        method: 'POST', headers: h(), credentials: 'include',
        body: JSON.stringify({ notes: notes.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message || (action === 'approve' ? 'Correction applied' : 'Correction rejected'));
      setReviewOpen(false);
      setReviewItem(null);
      load(filter);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this correction record? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/employee-corrections/${id}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      load(filter);
    } catch (e) { toast.error(e.message); }
  };

  const pendingCount = items.filter(i => i.status === 'pending').length;

  return (
    <Card data-testid="correction-requests-admin">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500" /> Profile Correction Requests
          {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700 ml-2">{pendingCount} pending</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={filter} onValueChange={setFilter} className="w-full">
          <TabsList className="grid grid-cols-4 max-w-md">
            <TabsTrigger value="pending" data-testid="corr-filter-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="corr-filter-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="corr-filter-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="corr-filter-all">All</TabsTrigger>
          </TabsList>
          <TabsContent value={filter} className="mt-4">
            {items.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No correction requests in this view.</p>
            ) : (
              <div className="space-y-2">
                {items.map(it => (
                  <div key={it.correction_id} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold">{it.employee_name}</span>
                          <span className="text-xs text-slate-400">({it.employee_id})</span>
                          <StatusBadge status={it.status} />
                        </div>
                        <p className="text-xs text-slate-500">{it.department || '—'} · {it.employee_email}</p>
                        <div className="mt-2 text-sm">
                          <span className="font-medium capitalize">{(it.field_name || '').replace(/_/g, ' ')}: </span>
                          <span className="text-slate-500 line-through">{String(it.current_value ?? '(empty)')}</span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold text-[#DC2626]">{String(it.proposed_value ?? '')}</span>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1"><span className="text-slate-400">Reason:</span> {it.reason}</p>
                        {it.reviewer_notes && <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Reviewer: {it.reviewer_notes}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">{new Date(it.submitted_at).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        {it.status === 'pending' && (
                          <>
                            <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => openReview(it)} data-testid={`review-corr-${it.correction_id}`}>Review</Button>
                          </>
                        )}
                        {isSuperAdmin && (
                          <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => remove(it.correction_id)} data-testid={`delete-corr-${it.correction_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Correction Request</DialogTitle>
          </DialogHeader>
          {reviewItem && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded">
                <p><strong>{reviewItem.employee_name}</strong> ({reviewItem.employee_id})</p>
                <p className="text-xs text-slate-500">{reviewItem.department} · {reviewItem.employee_email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Change request</p>
                <p className="capitalize"><strong>{(reviewItem.field_name || '').replace(/_/g, ' ')}</strong></p>
                <p className="mt-1">
                  <span className="text-slate-500 line-through">{String(reviewItem.current_value ?? '(empty)')}</span>
                  <span className="mx-2">→</span>
                  <span className="font-semibold text-green-700">{String(reviewItem.proposed_value ?? '')}</span>
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Reason</p>
                <p className="text-sm">{reviewItem.reason}</p>
              </div>
              <div>
                <label className="text-xs font-medium">Reviewer notes (optional for approve, required for reject)</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Add any context..." data-testid="reviewer-notes-input" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" onClick={() => decide('reject')} disabled={busy} data-testid="corr-reject-btn">Reject</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => decide('approve')} disabled={busy} data-testid="corr-approve-btn">
              {busy ? 'Working...' : 'Approve & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
